/**
 * Подтверждение необратимого действия.
 *
 * Удаление статуса уносит с собой историю задач, удаление поля — введённые
 * значения, отзыв ключа останавливает обмен. Всё это делалось одним нажатием
 * без вопроса, и промахнувшийся мышью узнавал о случившемся по последствиям.
 *
 * Спрашивается только о том, что нельзя вернуть: диалог на каждое действие
 * перестают читать через день, и тогда он не защищает ни от чего.
 *
 * Обещание вместо колбэка — чтобы место вопроса совпадало с местом действия:
 * `if (await confirm(…)) remove.mutate()` читается сверху вниз.
 */

import * as React from "react";

import { Button } from "@/shared/ui/Button";
import { Dialog } from "@/shared/ui/Overlays";

export interface ConfirmRequest {
  /** Заголовок отвечает на вопрос «что произойдёт»: «Удалить статус „Готово“?». */
  title: string;
  description?: string;
  /** Надпись на кнопке — глагол действия, а не «ОК». */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Опасное действие: кнопка красная. Обычное подтверждение — обычная. */
  tone?: "danger" | "default";
}

interface ConfirmContextValue {
  confirm: (request: ConfirmRequest) => Promise<boolean>;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

export function useConfirm(): ConfirmContextValue["confirm"] {
  const context = React.useContext(ConfirmContext);
  if (context === null) {
    throw new Error("useConfirm используется вне ConfirmProvider");
  }
  return context.confirm;
}

interface Pending {
  request: ConfirmRequest;
  settle: (answer: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [pending, setPending] = React.useState<Pending | null>(null);

  const confirm = React.useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setPending((current) => {
          // Второй вопрос поверх первого невозможен, но если он случится,
          // первый должен получить ответ, иначе вызвавший его код зависнет
          current?.settle(false);
          return { request, settle: resolve };
        });
      }),
    [],
  );

  const answer = React.useCallback((value: boolean) => {
    setPending((current) => {
      current?.settle(value);
      return null;
    });
  }, []);

  const value = React.useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {pending !== null && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              // Escape, крестик и щелчок мимо — это отказ, а не согласие
              answer(false);
            }
          }}
          title={pending.request.title}
          {...(pending.request.description === undefined
            ? {}
            : { description: pending.request.description })}
          footer={
            <>
              <Button
                variant="secondary"
                // Фокус на отказе, когда действие опасное: Enter по привычке
                // не должен удалять. Обычное подтверждение — наоборот
                autoFocus={pending.request.tone === "danger"}
                onClick={() => answer(false)}
              >
                {pending.request.cancelLabel ?? "Отмена"}
              </Button>
              <Button
                variant={pending.request.tone === "danger" ? "danger" : "primary"}
                autoFocus={pending.request.tone !== "danger"}
                onClick={() => answer(true)}
              >
                {pending.request.confirmLabel ?? "Удалить"}
              </Button>
            </>
          }
        />
      )}
    </ConfirmContext.Provider>
  );
}
